import { useEffect } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStartAPetitionPageData } from '#src/app/pages/startAPetitionV2/pageContext';
import { postDraftAiSchema, postDraftUserSchema, type PetitionDraft } from '#src/app/shared/api/petitionDraft';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useIsStarter } from '#src/app/shared/hooks/session';

export type SapLandingStep = 'intro' | 'create' | 'preview' | 'personal-story';

/**
 * URL axis — how the user arrived, derived from the `started_flow` param:
 * - `resolved`: already in the flow (`=true`) — render the current step in place.
 * - `deeplink`: an external caller routed directly to a step (present, not `true`) — honor it.
 * - `default`: any other entry (absent) — the draft decides the step.
 */
type EntryState = 'resolved' | 'deeplink' | 'default';

export function classifyEntry(startedFlowParam: string | undefined): EntryState {
	if (startedFlowParam === 'true') return 'resolved';
	if (startedFlowParam !== undefined) return 'deeplink';
	return 'default';
}

/**
 * Draft axis — which phase the draft was in when it decided the step:
 * `post_draft` (completed petition), `pre_draft` (in progress), `unparseable` (malformed → intro fallback).
 */
type DraftState = 'post_draft' | 'pre_draft' | 'unparseable';

/** A resolved draft landing: the step to enter on, plus which phase the draft was in. */
type DraftLanding = { landingStep: SapLandingStep; draftState: DraftState; parseException?: unknown };

/** A completed petition lands on its review/edit step; `null` means "not a completed draft". */
function resolvePostDraftLanding(draft: PetitionDraft): DraftLanding | null {
	const schema = draft.type === 'user_draft' ? postDraftUserSchema : postDraftAiSchema;
	if (!schema.safeParse(draft).success) return null;

	return { landingStep: draft.type === 'user_draft' ? 'create' : 'preview', draftState: 'post_draft' };
}

/** An in-progress (pre-)draft lands on its next input step. The experiment variant lives here. */
function resolvePreDraftLanding(draft: PetitionDraft, { isNewDraft }: { isNewDraft: boolean }): DraftLanding {
	const draftState = 'pre_draft';

	// EXPERIMENT(starfish_sap_chat_agent): chat_agent enters at the chat, which lives at `intro`.
	if (draft.metadata?.variant === 'chat_agent') return { landingStep: 'intro', draftState };

	// A carried-over issue prompt only skips the chooser on a fresh start (`new_draft`). A returning
	// user with a leftover prompt starts over at the chooser.
	if (isNewDraft && draft.type === 'ai_draft' && draft.input?.issuePrompt) {
		return { landingStep: 'personal-story', draftState };
	}

	return { landingStep: 'intro', draftState };
}

/**
 * Draft axis — the draft decides its landing: post-draft (completed) first, else pre-draft. The single
 * try/catch lives here: an unexpectedly malformed draft falls back to `intro`, is flagged `unparseable`,
 * and surfaces `parseException` for the shell to report.
 */
export function resolveDraftLanding(draft: PetitionDraft, { isNewDraft }: { isNewDraft: boolean }): DraftLanding {
	try {
		return resolvePostDraftLanding(draft) ?? resolvePreDraftLanding(draft, { isNewDraft });
	} catch (parseException) {
		return { landingStep: 'intro', draftState: 'unparseable', parseException };
	}
}

/**
 * Where to go, composing the two axes. `resolved` and `deeplink` bypass the draft entirely; only a
 * `default` entry consults it.
 */
export type EntryDecision =
	| { kind: 'resolved' }
	| { kind: 'deeplink' }
	| { kind: 'from_draft'; step: SapLandingStep; draftState: DraftState; parseException: unknown };

export function resolveEntry(
	startedFlowParam: string | undefined,
	draft: PetitionDraft,
	{ isNewDraft }: { isNewDraft: boolean },
): EntryDecision {
	const entry = classifyEntry(startedFlowParam);

	if (entry === 'resolved') return { kind: 'resolved' };
	if (entry === 'deeplink') return { kind: 'deeplink' };

	const { landingStep, draftState, parseException } = resolveDraftLanding(draft, { isNewDraft });
	return { kind: 'from_draft', step: landingStep, draftState, parseException: parseException ?? null };
}

/** Build the destination URL, stamping `started_flow=true` and consuming the one-shot `new_draft`. */
function buildEntryUrl(
	decision: Extract<EntryDecision, { kind: 'deeplink' | 'from_draft' }>,
	{ pathname, search }: { pathname: string; search: string },
): string {
	// Drop `new_draft` here — it's a one-shot entry signal, so it shouldn't ride along the flow.
	const { new_draft: newDraft, ...params } = qs.parse(search, { ignoreQueryPrefix: true });
	const query = qs.stringify({ ...params, started_flow: true });

	// honor the deeplink as-is, but stamp `started_flow=true` to mark it resolved for future renders.
	if (decision.kind === 'deeplink') return `${pathname}?${query}`;

	// for a draft-derived step, replace the last segment of the path with the resolved step.
	const pathWithoutStep = pathname.split('/').slice(0, -1).join('/');
	return `${pathWithoutStep}/${decision.step}?${query}`;
}

function formatUnknownForErrorReport(value: unknown): string {
	if (value instanceof Error) return value.message;
	if (typeof value === 'string') return value;
	return JSON.stringify(value);
}

export function useRedirectHandler() {
	const { data } = useStartAPetitionPageData();
	const { errorReporter } = useUtilityContext();
	const { pathname, search } = useLocation();
	const startedFlowParam = useQueryParamValue('started_flow');
	const sourceLocation = useQueryParamValue('source_location');
	const isNewDraft = useQueryParamValue('new_draft') === 'true';
	const isStarter = useIsStarter();

	const track = useTracking();
	const navigate = useNavigate();

	const decision = resolveEntry(startedFlowParam, data.draft, { isNewDraft });

	useEffect(() => {
		if (decision.kind === 'resolved') return;

		if (decision.kind === 'from_draft' && decision.parseException != null) {
			void errorReporter.report(
				{
					error: new Error('Failed to parse draft', { cause: decision.parseException }),
					params: {
						operation: 'useRedirectHandler',
						draftData: data.draft,
						parseError: formatUnknownForErrorReport(decision.parseException),
					},
				},
				{ severity: 'error' },
			);
		}

		// `sap_view` mirrors the entry decision: common properties always, plus the draft-axis
		// properties only when the draft decided the step (a deeplink's step is caller-chosen).
		const commonTrackingData = { entry_type: decision.kind, source_location: sourceLocation, is_starter: isStarter };
		void track('sap_view', {
			...commonTrackingData,
			...(decision.kind === 'from_draft' ? { draft_state: decision.draftState, landing_step: decision.step } : {}),
		});

		const entryUrl = buildEntryUrl(decision, { pathname, search });
		navigate(entryUrl, { replace: true, preventScrollReset: true });
	}, []);

	return { data: { isStepResolved: decision.kind === 'resolved' } };
}
